'use client';

import { useState } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ADDRESS_VARIANT_TYPE_LABELS,
  formatVariantAddress,
  useCreateAddressVariant,
  useDeleteAddressVariant,
  useIdentityVariants,
  useUpdateAddressVariant,
  type AddressVariant,
  type IdentityVariantOwnerType,
  type IdentityVariantsResponse
} from '@/lib/services/identity-variants';
import { IdentityVariantRow } from './identity-variant-row';
import { VariantSubsection } from './variant-subsection';
import { AddressVariantForm } from './address-variant-form';

/**
 * "Other addresses" — the previous and alternative addresses a person is or was
 * at (LF-5566 PROV-21), folded under the host form's address fields in LF-5714.
 *
 * This editor is currently the **only** way an address variant ever gets
 * created: extraction produces no multi-address signal, so every previous
 * address a share registry might match a holding against has to be captured by
 * hand here.
 *
 * Rows persist immediately through their own endpoints; they are **not** part of
 * the surrounding form's submit.
 */

interface OtherAddressesSubsectionProps {
  ownerType: IdentityVariantOwnerType;
  /** Undefined while the owner is unsaved — the subsection renders a hint instead. */
  ownerId: number | undefined;
  /** Storybook / test seam — bypasses the query so the subsection renders given data. */
  data?: IdentityVariantsResponse;
}

export function OtherAddressesSubsection({
  ownerType,
  ownerId,
  data: dataOverride
}: OtherAddressesSubsectionProps) {
  const owner = { ownerType, ownerId };
  /** The row being edited, `'new'` while adding, or null. */
  const [editing, setEditing] = useState<AddressVariant | 'new' | null>(null);
  const close = () => setEditing(null);

  const query = useIdentityVariants(owner, {
    enabled: !dataOverride && !!ownerId
  });
  const data = dataOverride ?? query.data;

  const create = useCreateAddressVariant(owner);
  const update = useUpdateAddressVariant(owner);
  const remove = useDeleteAddressVariant(owner);

  /*
   * Primary rows are excluded, matching the names subsection. A primary address
   * variant duplicates the owner's own address field directly above this
   * subsection — so it is not an "other" address, and listing it contradicted
   * the heading. Unlike names there is no backend filter to mirror: `isPrimary`
   * on an address variant has **no consumer at all** (`registryIdentityUnion`
   * renders every variant unfiltered), which is why the flag is no longer
   * settable from the form either.
   */
  const variants = (data?.addressVariants ?? []).filter(
    (variant) => !variant.isPrimary
  );

  if (!ownerId) {
    return (
      <p className="text-muted-foreground text-xs">
        Save this {ownerType === 'matter' ? 'matter' : 'contact'} first to
        record other addresses.
      </p>
    );
  }

  const isBusy = create.isPending || update.isPending;

  if (query.isLoading && !dataOverride) {
    return <Skeleton className="h-4 w-32" />;
  }

  return (
    <VariantSubsection
      label="Other addresses"
      addLabel="Add address"
      count={variants.length}
      onAdd={() => setEditing('new')}
      disabled={isBusy}
      isEmpty={variants.length === 0 && !editing}
      emptyHint="No other addresses recorded."
    >
      <>
        {variants.map((variant) =>
          editing !== 'new' && editing?.id === variant.id ? (
            <AddressVariantForm
              key={`edit-${variant.id}`}
              variant={variant}
              isSubmitting={update.isPending}
              onCancel={close}
              onSubmit={(payload) =>
                update.mutateAsync({ id: variant.id, data: payload })
              }
            />
          ) : (
            <IdentityVariantRow
              key={variant.id}
              value={formatVariantAddress(variant.value)}
              meta={[
                ADDRESS_VARIANT_TYPE_LABELS[variant.type],
                !variant.isCurrent && 'previous',
                variant.periodNote
              ]}
              provenance={variant.provenance}
              entityLabel="address"
              disabled={isBusy}
              onEdit={() => setEditing(variant)}
              onDelete={async () => {
                await remove.mutateAsync(variant.id);
              }}
            />
          )
        )}
        {editing === 'new' && (
          <AddressVariantForm
            isSubmitting={create.isPending}
            onCancel={close}
            onSubmit={(payload) => create.mutateAsync(payload)}
          />
        )}
      </>
    </VariantSubsection>
  );
}
